import { StatusLight } from '@react-spectrum/s2';
import type { ReactNode } from 'react';

import { TYPE_TAGS } from '@/components/artifact/panelMeta';
import { getPanelIcon } from '@/spaces/chat/messages/panelTypeIcons';
import {
	OUTCOME_LABEL,
	OUTCOME_VARIANT,
} from '@/spaces/chat/panels/ownerActionOutcome';
import {
	scheduleItemTitle,
	statusToRowLabel,
	triggerSummary,
} from '@/spaces/chat/panels/scheduleUtils';

import type { Action as ProcessAction } from '../../../../bridge/types/action.ts';
import type { Artifact } from '../../../../bridge/types/artifact.ts';
import {
	actionForArtifact,
	isPlannedArtifact,
} from '../../../../bridge/utils/actionBindings.ts';
import {
	getContentArtifacts,
	getMonitorArtifacts,
	getOwnerActions,
	latestOwnerActionResponse,
	notSkipped,
	type ProcessChildrenView,
} from '../../../../bridge/utils/processClassification.ts';

import { SectionLabel } from './StrategyBits';
import { useProcessChildren } from './useProcessChildren';

// How many un-typed leftovers the card will draw before it summarises the rest. Generous on
// purpose: a real campaign's planned work has to fit under it whole, so the cap only ever bites
// on run-time output.
const OTHER_CONTENT_MAX = 12;

/** One line of real work. Deliberately not `SetupCardRow` and deliberately not
 * `ArtifactRow`: this list is read-only, so it must not carry a chevron promising
 * an editor, and it must not grey itself out the way `SetupCardRow`'s inert path
 * does — a muted list reads as disabled, which is the opposite of "here is what
 * we made you". */
const BuiltRow = ({
	icon,
	title,
	clamp = false,
	trailing,
}: {
	icon: ReactNode;
	title: string;
	/** Two clamped lines instead of one truncated one. The owner's ask uses it, because its
	 *  text IS a brief rather than a label. Clamping is presentational, so the whole brief
	 *  stays in the DOM for a reader and for a test. */
	clamp?: boolean;
	trailing: ReactNode;
}) => (
	<div className="flex items-center gap-3 py-1.5">
		<div
			className="flex size-8 shrink-0 items-center justify-center text-gray-600
				[&_svg]:size-5"
		>
			{icon}
		</div>
		<div className="flex min-w-0 flex-1 flex-col justify-center text-left">
			<span
				className={
					clamp
						? 'line-clamp-2 text-sm text-(--text-primary)'
						: 'truncate text-sm font-medium text-(--text-primary)'
				}
			>
				{title}
			</span>
		</div>
		<span
			className="shrink-0 self-center text-right text-xs text-(--text-muted)"
		>
			{trailing}
		</span>
	</div>
);

/**
 * What a campaign actually built — the posts, the emails, the owner's part, the
 * ongoing jobs — read off the Process behind it.
 *
 * This replaces the planner's abstract steps once a campaign is materialized. The
 * sketch describes intent; by the time an owner is deciding, the intent has been
 * turned into specific content on a specific schedule, and that is what they
 * should be judging.
 */
export function CampaignBuiltWork({
	processId,
	isLive,
}: {
	processId: string;
	/** Built-but-not-started vs running. Picks which schedule vocabulary is
	 * truthful — a `proposed` artifact has no status worth printing, a live one
	 * does. */
	isLive: boolean;
}) {
	const { artifacts, actions, state } = useProcessChildren(processId);

	if (state === 'idle') return null;

	if (state === 'loading') {
		return (
			<div className="space-y-2" data-testid="campaign-built-work-loading">
				{[0, 1].map((i) => (
					<div key={i} className="flex items-center gap-3 py-1.5">
						<div className="size-8 shrink-0 animate-pulse rounded bg-gray-100" />
						<div className="h-4 flex-1 animate-pulse rounded bg-gray-100" />
					</div>
				))}
			</div>
		);
	}

	if (state === 'error') {
		return (
			<p
				data-testid="campaign-built-work-error"
				className="text-sm text-(--text-muted)"
			>
				We couldn&apos;t load what we built for this campaign.
			</p>
		);
	}

	// `/processes/{id}/children` does not return the skip lists — they live on the
	// process document. A strategy campaign's Process never passes through the
	// setup card (go-live activates it directly), so nothing is ever skipped on
	// one; passing them explicitly rather than dropping the concept means the day
	// that route starts returning them, this is a one-line change.
	const view: ProcessChildrenView = {
		children: artifacts,
		actions,
		skipped_artifact_ids: [],
		skipped_action_ids: [],
	};

	const { posts, emails } = getContentArtifacts(view);
	const ownerActions = getOwnerActions(view);
	const monitors = getMonitorArtifacts(view);

	// EXHAUSTIVE by construction. `getContentArtifacts` returns only `instagram_post` and the two
	// email types, and `getMonitorArtifacts` only the three monitor types. Every other child
	// matched nothing and rendered nowhere — `digital_ad`, `instagram_dm` and
	// `instagram_comment_reply` were all built for the owner and then never shown. Taking the
	// LEFTOVERS rather than naming the missing types means the next artifact type added to the
	// planner cannot disappear the same way.
	const accounted = new Set<string>([
		...posts.map((a) => a.id),
		...emails.map((a) => a.id),
		...monitors.map((a) => a.id),
	]);
	// The leftovers are also the ONLY selector here that can grow at run time, and that is the
	// price of taking them. The comment monitor mints one `instagram_comment_reply` per incoming
	// comment and attaches it to this same Process, so a busy campaign added a row for ever
	// (ADO-1984). Two bounds, and neither one NAMES a type — naming types is what made
	// `digital_ad`, `instagram_dm` and `instagram_comment_reply` vanish in the first place:
	//   1. the children the PLAN binds sort FIRST, so run-time output can never push built work
	//      out of the visible rows;
	//   2. a cap, with a "+N more" row, so the section has a maximum height whatever happens.
	// Nothing is dropped. A child with no binding is still counted and still reachable.
	const otherContent = (view.children ?? [])
		.filter(notSkipped(view))
		.filter((a) => !accounted.has(a.id));
	const plannedFirst = [
		...otherContent.filter((a) => isPlannedArtifact(actions, a)),
		...otherContent.filter((a) => !isPlannedArtifact(actions, a)),
	];
	const shownOther = plannedFirst.slice(0, OTHER_CONTENT_MAX);
	const hiddenOtherCount = plannedFirst.length - shownOther.length;
	const content: Artifact[] = [...posts, ...emails, ...shownOther];
	const monitorActions = actions.filter(
		(a) => a.type === 'monitor_instagram_comments_and_reply',
	);

	const nothing =
		content.length === 0 &&
		ownerActions.length === 0 &&
		monitors.length === 0 &&
		monitorActions.length === 0;
	// A built Process with no children is a backend anomaly, not a thing to
	// announce. Render nothing and let the caller keep the plan on screen.
	if (nothing) return null;

	// The artifact rides along to `triggerSummary` because the trigger alone cannot
	// pick the verb: one `scheduled_once` shape drives both a post and an email, and
	// this list mixes them. With it, a dated post reads "Publishes Oct 2" and a
	// dated email "Sends Oct 2".
	const scheduleFor = (artifact: Artifact): string => {
		const action = actionForArtifact(actions, artifact);
		return isLive
			? statusToRowLabel(artifact, action, 'active').label
			: triggerSummary(action?.trigger, artifact);
	};

	// A status only where it SAYS something. Every unanswered row read "Waiting on you", under a
	// heading that already reads "Your part" — four identical pills carrying no information each.
	// An answered row still shows its outcome, so the pill now means "this one is settled".
	const ownerAnswer = (action: ProcessAction): ReactNode => {
		const response = latestOwnerActionResponse(action);
		if (!response) return null;
		return (
			<StatusLight variant={OUTCOME_VARIANT[response.outcome]} size="S">
				{OUTCOME_LABEL[response.outcome]}
			</StatusLight>
		);
	};

	return (
		<div data-testid="campaign-built-work" className="space-y-4">
			{content.length > 0 && (
				<div className="space-y-1">
					<SectionLabel>
						{isLive ? 'Publishing' : "What we'll publish"}
					</SectionLabel>
					{content.map((a) => (
						<BuiltRow
							key={a.id}
							icon={getPanelIcon(a.type)}
							title={scheduleItemTitle(a)}
							trailing={scheduleFor(a)}
						/>
					))}
					{hiddenOtherCount > 0 && (
						// A count, not a control: this list is read-only, and the campaign panel is
						// where the whole set is read.
						<p
							data-testid="campaign-built-work-more"
							className="py-1.5 pl-11 text-sm text-(--text-muted)"
						>
							{`+${hiddenOtherCount} more`}
						</p>
					)}
				</div>
			)}

			{ownerActions.length > 0 && (
				<div className="space-y-1">
					<SectionLabel>Your part</SectionLabel>
					{ownerActions.map((action) => {
						const instruction = (
							action.config as { instruction?: string } | undefined
						)?.instruction;
						return (
							<BuiltRow
								key={action.id}
								icon={getPanelIcon('request_owner_action')}
								// The BRIEF, and only the brief. This row used to print a derived
								// title above it, and `ownerActionRowTitle` derives that title from
								// the brief's own first sentence — so every ask rendered its opening
								// words twice, once clipped and once in full. design.md forbids
								// exactly that: a surface whose body already says what the thing
								// does must not repeat it under a title.
								title={instruction?.trim() || 'Your part'}
								clamp
								trailing={ownerAnswer(action)}
							/>
						);
					})}
				</div>
			)}

			{(monitors.length > 0 || monitorActions.length > 0) && (
				<div className="space-y-1">
					<SectionLabel>Runs continuously</SectionLabel>
					{monitors.map((m) => (
						<BuiltRow
							key={m.id}
							icon={getPanelIcon(m.type)}
							title={scheduleItemTitle(m) || TYPE_TAGS[m.type] || 'Ongoing job'}
							trailing="Runs continuously"
						/>
					))}
					{monitorActions.map((a) => (
						<BuiltRow
							key={a.id}
							icon={getPanelIcon('ig_comment_watcher_class')}
							title="Watch and reply to comments"
							trailing="Runs continuously"
						/>
					))}
				</div>
			)}
		</div>
	);
}
